import { useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { Button } from "@/libraries/design/components/button/src/button.tsx";
import { Modal } from "@/libraries/design/components/modal/src/modal.tsx";
import { wacToken } from "@/libraries/design/foundations/index.ts";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles.tsx";

import {
    useClearSelectedFeature,
    useSelectedFeatureData
} from "../context/hooks.ts";

import { FeatureDescription } from "./feature-description.tsx";
import { StatusLozenge } from "./status-lozenge.tsx";

export const SelectedFeatureModal = () => {
    const selectedFeature = useSelectedFeatureData();
    const clearSelectedFeature = useClearSelectedFeature();
    const [isCopied, setIsCopied] = useState(false);

    const intl = useIntl();

    const header = (
        <div>
            <span
                css={css({
                    margin: 0,
                    fontWeight: 900
                })}
            >
                {selectedFeature?.category.roadmapCategoryNameExternal}
            </span>
            <h4
                css={css({
                    fontSize: "20px",
                    marginBlockEnd: 0
                })}
            >
                {selectedFeature?.title}
            </h4>
        </div>
    );

    return (
        <TypographyStyles>
            <Modal
                isOpen={!!selectedFeature}
                closeModal={clearSelectedFeature}
                title={header}
                closeLabel={intl.formatMessage({
                    id: "SelectedFeatureModal.close",
                    defaultMessage: "Close"
                })}
            >
                {selectedFeature && (
                    <>
                        <FeatureDescription
                            content={selectedFeature.description}
                        />

                        <div
                            css={css({
                                display: "flex",
                                flexDirection: "column",
                                gap: "16px"
                            })}
                        >
                            <div>
                                {intl.formatMessage({
                                    id: "SelectedFeatureModal.share",
                                    defaultMessage: "Share this update:"
                                })}
                                <div
                                    css={css({
                                        display: "flex",
                                        gap: "16px"
                                    })}
                                >
                                    <input
                                        readOnly={true}
                                        value={getShareableUrl()}
                                        css={css({
                                            maxWidth: "calc(100% - 141px)",
                                            lineHeight: "35px",
                                            height: "40px",
                                            border: `2px solid ${wacToken(
                                                "color.neutral.light400"
                                            )}`,
                                            borderRadius: "3px",
                                            padding: "0 8px",
                                            position: "relative",
                                            insetBlockStart: "-1px"
                                        })}
                                    />
                                    <Button
                                        onClick={() => {
                                            navigator.clipboard.writeText(
                                                getShareableUrl()
                                            );
                                            setIsCopied(true);
                                            setTimeout(() => {
                                                setIsCopied(false);
                                            }, 2000);
                                        }}
                                    >
                                        {isCopied
                                            ? intl.formatMessage({
                                                  id: "SelectedFeatureModal.linkCopied",
                                                  defaultMessage: "Link Copied"
                                              })
                                            : intl.formatMessage({
                                                  id: "SelectedFeatureModal.copyLink",
                                                  defaultMessage: "Copy link"
                                              })}
                                    </Button>
                                </div>
                            </div>

                            <div css={css({ display: "flex", gap: 4 })}>
                                <StatusLozenge
                                    text={
                                        selectedFeature.status
                                            .roadmapStatusNameExternal
                                    }
                                />
                                <span>{selectedFeature.timeFrame}</span>
                            </div>

                            <div>
                                {intl.formatMessage({
                                    id: "SelectedFeatureModal.availableIn",
                                    defaultMessage:
                                        "This update is available in:"
                                })}
                                &nbsp;
                                {selectedFeature.appsCollection.items
                                    .map((item) => item.roadmapAppNameExternal)
                                    .join(", ")}
                            </div>
                        </div>
                    </>
                )}
            </Modal>
        </TypographyStyles>
    );
};

function getShareableUrl() {
    return window.location.href;
}
